import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import propertiesService from '../../services/propertiesService';
import DateTimeModal from '../DateTimeModal';
import userRoleUtils from '../../utils/userRoleUtils';
import DefaultOrder from '../DefaultOrder';
import { useCart } from '../../context/CartContext';
import { notification } from 'antd';
import userService from '../../services/userService';

const getUserId = selectedUser => {
  if (selectedUser && typeof selectedUser === 'object') return selectedUser.id ?? null;
  if (selectedUser) return selectedUser;
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.id ?? null;
  } catch (error) {
    return null;
  }
};

const NewPropertyForm = ({ onSave, onSaveAndAddToCart, selectedUser = null, onClose,setShowNewPropertyForm }) => {
  const canManageSchedoxSync = userRoleUtils.canManageSchedoxSync();
  const { addToCart, refreshCart } = useCart();
  const syncUserId = getUserId(selectedUser);
  const [userSyncEnabled, setUserSyncEnabled] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [formData, setFormData] = useState({
    type: 1,
    property_type: '',
    userid: selectedUser ?? null,
    property_title: '',
    contact_name: '',
    contact_number: '',
    appartment_address: '',
    appartment_number: '',
    post_code: '',
    delivery_city: '',
    delivery_country: '',
    arrival_times: ['08', '00'],
    completion_times: ['09', '30'],
    date: getTodayDate(),
    quantity: {},
    product_quantity: {},
    cleaner_information: '',
    property_description: '',
    apartment_name: '',
    sync_to_schedox: false,
    schedox_service_id: '',
  });

      const [propertyTypes, setPropertyTypes] = useState([]);
      const [countries, setCountries] = useState([]);
      const [categories, setCategories] = useState([]);
      const [products, setProducts] = useState({});
      const [packs, setPacks] = useState([]);
      const [rooms, setRooms] = useState([]);
      const [schedoxServices, setSchedoxServices] = useState([]);
      const [isDateTimeModalOpen, setIsDateTimeModalOpen] = useState(false);

      const minDate = userRoleUtils.getMinimumOrderDate();
    
     
    
   const handleDateTimeModalClose = () => {
    setIsDateTimeModalOpen(false);
  };

  const handleDateTimeModalSave = (updatedDateTime) => {
    // Extract hours and minutes from arrival_time and completion_time
    let arrivalTimes = ['08', '00'];
    if (updatedDateTime.arrival_time && updatedDateTime.arrival_time.includes(':')) {
      arrivalTimes = updatedDateTime.arrival_time.split(':');
    }
    
    let completionTimes = ['09', '30'];
    if (updatedDateTime.completion_time && updatedDateTime.completion_time.includes(':')) {
      completionTimes = updatedDateTime.completion_time.split(':');
    }

    const minDate = new Date(userRoleUtils.getMinimumOrderDate());
    
    setFormData(prev => ({
      ...prev,
      arrival_times: arrivalTimes,
      completion_times: completionTimes,
      date: updatedDateTime.order_date || minDate.toISOString().split('T')[0]
    }));
    
    setIsDateTimeModalOpen(false);
  };

  useEffect(() => {
    // Check if the current date is less than minDate
    const currentDate = new Date(formData.date);
    if (currentDate < minDate) {
      // Update the date to minDate if it's less than minimum
      handleInputChange({
        target: {
          name: 'date',
          value: minDate.toISOString().split('T')[0]
        }
      });
    }
  }, [formData.date]);

      useEffect(() => {
        fetchAddPropertyData();
      }, []);

      useEffect(() => {
        let active = true;
        if (!canManageSchedoxSync || !syncUserId) {
          setUserSyncEnabled(false);
          return () => { active = false; };
        }

        userService.getSchedoxSyncStatus(syncUserId)
          .then(response => {
            if (!active) return;
            const status = response?.data || response;
            const enabled = Boolean(status?.sources?.user_toggle);
            setUserSyncEnabled(enabled);
            setFormData(current => ({ ...current, sync_to_schedox: enabled }));
          })
          .catch(() => {
            if (active) setUserSyncEnabled(false);
          });

        return () => { active = false; };
      }, [syncUserId, canManageSchedoxSync]);
    
      const fetchAddPropertyData = async () => {
        let response = await propertiesService.addProperty();
        if (response) {
          setPropertyTypes(response.propertytype);
          setCountries(response.countries);
      
          // Initialize rooms with 0 count
          const initialRooms = {};
          response.rooms.forEach(room => {
            initialRooms[room.id] = 0;
          });
          setCategories(response.categories);
          setProducts(response.products || {});
          setPacks(response.packs || []);
          setRooms(response.rooms || []);
          setSchedoxServices(response.schedox_services || []);
    
          setFormData(prevData => ({
            ...prevData,
            quantity: initialRooms
          }));
        }
      };
    
      const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'arrival_time' || name === 'completion_time') {
          const [hours, minutes] = value.split(':');
          setFormData(prevData => ({
            ...prevData,
            [`${name}s`]: [hours, minutes]
          }));
        } else {
          setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
        }
      };
    
      const handleQuantityChange = (itemId, change, isProduct, price) => {
        setFormData(prevData => {
          const currentQuantity = prevData.product_quantity[itemId]?.qty || 0;
          // Convert to number and ensure we're working with floats
          const numCurrentQuantity = parseFloat(currentQuantity);
          const numChange = parseFloat(change);
          // Calculate new quantity with precision handling
          const newQuantity = Math.max(0, numCurrentQuantity + numChange);
          // Round to 2 decimal places
          const roundedQuantity = Math.round(newQuantity * 100) / 100;
          
          if (roundedQuantity === 0) {
            const { [itemId]: _, ...rest } = prevData.product_quantity;
            return { ...prevData, product_quantity: rest };
          }
    
          return {
            ...prevData,
            product_quantity: {
              ...prevData.product_quantity,
              [itemId]: { 
                id: itemId,
                price: price,
                type: isProduct ? 'product' : 'pack',
                qty: roundedQuantity
              }
            }
          };
        });
      };
    
      const handleRentTypeChange = (productId, rentType) => {
        setFormData(prevData => ({
          ...prevData,
          product_quantity: {
            ...prevData.product_quantity,
            [productId]: { ...prevData.product_quantity[productId], rentType }
          }
        }));
      };

      const handleAddExtraItem = (extraItem) => {
        setFormData(prevData => ({
          ...prevData,
          product_quantity: {
            ...prevData.product_quantity,
            [extraItem.id]: {
              id: extraItem.id,
              name: extraItem.name,
              qty: extraItem.qty,
              price: extraItem.price,
              isExtra: true,
              type: 'extra'
            }
          }
        }));
      };

      const handleRemoveExtraItem = (extraItemId) => {
        setFormData(prevData => {
          const nextProductQuantity = { ...prevData.product_quantity };
          delete nextProductQuantity[extraItemId];
          return { ...prevData, product_quantity: nextProductQuantity };
        });
      };

      const handleDiscountChange = ({ enabled, type, value }) => {
        setFormData(prevData => {
          const nextItems = { ...prevData.product_quantity };
          if (!enabled || Number(value) <= 0) {
            delete nextItems['discount-default'];
          } else {
            nextItems['discount-default'] = {
              id: 'discount-default',
              name: 'Default order discount',
              qty: 1,
              price: 0,
              type: 'discount',
              discount_type: type,
              discount_value: Number(value)
            };
          }
          return { ...prevData, product_quantity: nextItems };
        });
      };
    
      const handleRoomChange = (roomId, value) => {
        setFormData(prevData => ({
          ...prevData,
          quantity: { ...prevData.quantity, [roomId]: parseInt(value) }
        }));
      };
    
 
      const prepareDataToSend = () => {
        const dataToSend = new FormData();

        //add userid if is Admin from user
    
        // Add _token (you might need to get this from somewhere)
     
        // Add basic fields
        Object.entries(formData).forEach(([key, value]) => {
          if (!canManageSchedoxSync && ['sync_to_schedox', 'schedox_service_id'].includes(key)) return;
          if (!['quantity', 'product_quantity', 'arrival_times', 'completion_times'].includes(key)) {
            dataToSend.append(key, value);
          }
        });
    
        // Add arrival and completion times
        formData.arrival_times.forEach(time => dataToSend.append('arrival_times[]', time));
        formData.completion_times.forEach(time => dataToSend.append('completion_times[]', time));
    
        // Add quantity
        Object.entries(formData.quantity).forEach(([roomId, count]) => {
          dataToSend.append(`quantity[${roomId}]`, count);
        });
    
        // Add product_quantity
        Object.entries(formData.product_quantity).forEach(([productId, product]) => {
          Object.entries(product).forEach(([field, value]) => {
            dataToSend.append(`product_quantity[${productId}][${field}]`, value);
          });
        });
    
        return dataToSend;
      };
    
      const handleSave = () => {
        const dataToSend = prepareDataToSend();
        dataToSend.set('type', '1'); // Assuming type 1 is for save
        dataToSend.set('userid', selectedUser ?? null);

        onSave(dataToSend);
      };
    
 const handleSaveAndAddToCart = async () => {
       const dataToSend = prepareDataToSend();
      dataToSend.set('userid', selectedUser ?? null);
      dataToSend.set('type', '2');

    
        onSave(dataToSend);
   };

  return (
    <div className="border rounded-lg overflow-y-auto px-6 pt-6 pb-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

      {/* About Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">About</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Property ID*</label>
              <input
                type="text"
                name="property_title"
                value={formData.property_title}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Property Type*</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              >
                <option value="">Select Type</option>
                {propertyTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.title}</option>
                ))}
              </select>
            </div>
            {canManageSchedoxSync && <>
            <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
                <input
                  type="checkbox"
                  name="sync_to_schedox"
                  checked={Boolean(formData.sync_to_schedox)}
                  onChange={handleInputChange}
                  disabled={!userSyncEnabled}
                  className="mt-1 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700">Sync to Schedox</span>
                <span className="block text-xs text-gray-500">
                  {userSyncEnabled
                    ? 'Enabled from the user setting; switch this property off here if it should be excluded.'
                    : 'Enable Sync to Schedox on the user before enabling this property.'}
                </span>
              </span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700">Schedox Service Type</label>
              <select
                name="schedox_service_id"
                value={formData.schedox_service_id}
                onChange={handleInputChange}
                disabled={!userSyncEnabled || !formData.sync_to_schedox}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue disabled:bg-gray-100"
              >
                <option value="">Use Schedox default service</option>
                {schedoxServices.map(service => (
                  <option key={service.service_id} value={service.service_id}>
                    {service.name}{service.code ? ` (${service.code})` : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                The selected Schedox service supplies the booking type and checklist; Golden order hours and price stay authoritative.
              </span>
            </div>
            </>}
            <div>
            <label className="block text-sm font-medium text-gray-700">Property Description</label>
            <textarea
              name="property_description"
              value={formData.property_description}
              onChange={handleInputChange}
              rows="3"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
            ></textarea>
          </div>
          </div>
        </div>
      </div>
      {/* Contact Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">Contact</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Name*</label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Access</label>
              <input
                type="text"
                name="cleaner_information"
                value={formData.cleaner_information}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Number*</label>
              <input
                type="text"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Description</label>
            <textarea
              name="contact_description"
              value={formData.contact_description}
              onChange={handleInputChange}
              rows="3"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
            ></textarea>
          </div>
        </div>
      </div>

      {/* Rooms Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">Rooms</h3>
        <div className="space-y-4">
          {Object.entries(formData.quantity).map(([roomId, count]) => (
            <div key={roomId} className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 capitalize">{rooms.find((r) => r.id === parseInt(roomId))?.room_title}</span>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => handleRoomChange(roomId, Math.max(0, count - 1))}
                  className="bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center focus:outline-none"
                >
                  -
                </button>
                <span className="mx-2 w-8 text-center">{count}</span>
                <button
                  type="button"
                  onClick={() => handleRoomChange(roomId, count + 1)}
                  className="bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center focus:outline-none"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Default Timeslot Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">Default Timeslot</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Arrival Time*</label>
              <input
                type="time"
                readOnly
                value={`${formData.arrival_times[0].padStart(2, '0')}:${formData.arrival_times[1].padStart(2, '0')}`}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                onClick={() => setIsDateTimeModalOpen(true)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Completion Time*</label>
              <input
                type="time"
                readOnly
                value={`${formData.completion_times[0].padStart(2, '0')}:${formData.completion_times[1].padStart(2, '0')}`}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
                onClick={() => setIsDateTimeModalOpen(true)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              readOnly
              value={formData.date}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              onClick={() => setIsDateTimeModalOpen(true)}
            />
          </div>
        </div>
      </div>


        <div className="bg-white p-6 rounded-lg shadow col-span-2 border">
          <h3 className="text-xl font-semibold mb-4">Address</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Address*</label>
              <input
                type="text"
                name="appartment_address"
                value={formData.appartment_address}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Apartment/Building No*</label>
              <input
                type="text"
                name="appartment_number"
                value={formData.appartment_number}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
              />
            </div>
            
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">

<div>
  <label className="block text-sm font-medium text-gray-700">
    City*
  </label>
  <input
    type="text"
    name="delivery_city"
    value={formData.delivery_city}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  />
</div>
<div>
  <label className="block text-sm font-medium text-gray-700">
    Country*
  </label>
  <select
    name="delivery_country"
    value={formData.delivery_country}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  >
    <option value="">Select Country</option>
    {countries.map((country) => (
      <option key={country.id} value={country.sortname}>
        {country.name}
      </option>
    ))}
  </select>
</div>
<div>
  <label className="block text-sm font-medium text-gray-700">
    Post Code*
  </label>
  <input
    type="text"
    name="post_code"
    value={formData.post_code}
    onChange={handleInputChange}
    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-golden-blue focus:border-golden-blue"
  />
</div>
</div>
        </div>
      </div>
      <DateTimeModal
        isOpen={isDateTimeModalOpen}
        onClose={handleDateTimeModalClose}
        propertyId={formData.property_id}
        isSaveHidden={true}
        initialData={{
          arrival_time: formData.arrival_times.join(':'),
          completion_time: formData.completion_times.join(':'),
          order_date: formData.date
        }}
        onSave={handleDateTimeModalSave}
      />
      <DefaultOrder 
        categories={categories} 
        products={products} 
        packs={packs}
        defaultOrder={formData.product_quantity}
        onAddExtraItem={handleAddExtraItem}
        onRemoveExtraItem={handleRemoveExtraItem}
        onDiscountChange={handleDiscountChange}
        onQuantityChange={handleQuantityChange}
        onRentTypeChange={handleRentTypeChange}
      />

      <div className="mt-6 flex justify-end space-x-4">
        <button
          type="button"
          onClick={handleSave}
          className="bg-golden-navy text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors duration-300"
        >
            Save Property Info
        </button>
        <button
          type="button"
          onClick={handleSaveAndAddToCart}
          className="bg-golden-blue text-white px-6 py-2 rounded-lg hover:bg-opacity-100 transition-colors duration-300"
        >
          Save Property Info & Add to Cart
        </button>
      </div>
    </div>
  );
};

export default NewPropertyForm;
